// Application Context Provider
// Manages project state, PFMEA data, and page context
// ============================================================
// UPDATED: Added dynamic project loading and RBAC awareness
// ============================================================

import React, { createContext, useContext, useState, useCallback } from 'react';
import { PfmeaItem, ProcessFlowDiagram, ControlPlan, PfmeaStandard, DashboardMetrics, PageContext, PAGE_CONTEXTS, WorkflowStep, AIAG_VDA_WORKFLOW, ProjectScoping, ProcessFunction, ProductRequirement } from '../types';
import { STANDARD_CONFIGS, StandardConfig, calculateActionPriority, calculateRPN } from '../constants/standards';
// Seed data import removed — all data loaded live from Firebase via backend API
import { useLocation } from 'react-router-dom';
import { runAiagVdaAudit } from '../utils/aiagVdaAuditor';
import { loadProject, PfmeaProject } from '../services/projectService';


interface AppContextType {
    // Project Management
    activeProject: string | null;  // Display name for UI
    setActiveProject: (project: string) => void;
    activeProjectName: string | null;  // Internal name for API calls
    setActiveProjectName: (name: string) => void;
    projects: string[];

    // Dynamic Project Loading (NEW)
    loadProjectData: (projectName: string) => Promise<boolean>;
    clearProjectData: () => void;
    isProjectLoaded: boolean;

    // Standard Configuration
    activeStandard: PfmeaStandard;
    setActiveStandard: (standard: PfmeaStandard) => void;
    standardConfig: StandardConfig;

    // PFMEA Data
    pfmeaItems: PfmeaItem[];
    setPfmeaItems: (items: PfmeaItem[]) => void;
    addPfmeaItem: (item: PfmeaItem) => void;
    updatePfmeaItem: (id: string, updates: Partial<PfmeaItem>) => void;
    deletePfmeaItem: (id: string) => void;

    // Process Flow
    processFlow: ProcessFlowDiagram | null;
    setProcessFlow: (flow: ProcessFlowDiagram | null) => void;

    // Control Plan
    controlPlans: ControlPlan[];
    setControlPlans: (plans: ControlPlan[]) => void;

    // AIAG-VDA Data
    scoping: ProjectScoping | undefined;
    setScoping: (scoping: ProjectScoping | undefined) => void;
    processFunctions: ProcessFunction[];
    setProcessFunctions: (functions: ProcessFunction[]) => void;
    productRequirements: ProductRequirement[];
    setProductRequirements: (requirements: ProductRequirement[]) => void;

    // Dashboard Metrics
    metrics: DashboardMetrics;

    // Workflow Progress
    workflowSteps: WorkflowStep[];

    // Page Context for AI Assistant
    currentPageContext: PageContext | null;

    // Loading States
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;

    // Full Screen State
    isFullScreen: boolean;
    setIsFullScreen: (fullScreen: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();

    // Project State
    // activeProject = display name shown in UI
    // activeProjectName = internal name used for API calls (e.g., "Aerator_Pro")
    const [activeProject, setActiveProject] = useState<string | null>(null);
    const [activeProjectName, setActiveProjectName] = useState<string | null>(null);
    const [isProjectLoaded, setIsProjectLoaded] = useState<boolean>(false);
    const [projects] = useState<string[]>([]); // Populated from backend via fetchProjects()

    // Standard Configuration
    const [activeStandard, setActiveStandard] = useState<PfmeaStandard>('AIAG_VDA');

    // PFMEA Data - empty until a project is loaded from Firebase/backend
    const [pfmeaItems, setPfmeaItems] = useState<PfmeaItem[]>([]);
    const [processFlow, setProcessFlow] = useState<ProcessFlowDiagram | null>(null);
    const [controlPlans, setControlPlans] = useState<ControlPlan[]>([]);

    // Scoping, Functions, and Requirements (for AIAG-VDA Audit) - empty until project loaded
    const [scoping, setScoping] = useState<ProjectScoping | undefined>(undefined);
    const [processFunctions, setProcessFunctions] = useState<ProcessFunction[]>([]);
    const [productRequirements, setProductRequirements] = useState<ProductRequirement[]>([]);

    // Loading State
    const [isLoading, setIsLoading] = useState(false);

    // Full Screen State
    const [isFullScreen, setIsFullScreen] = useState(false);

    // ============================================================
    // DYNAMIC PROJECT LOADING (NEW)
    // ============================================================

    /**
     * Load project data from the backend API
     * Updates all relevant state with the loaded data
     * @param projectName - Internal project name for API call
     * @returns true if successful, false if failed
     */
    const loadProjectData = useCallback(async (projectName: string): Promise<boolean> => {
        console.log(`[AppContext] Loading project: ${projectName}`);
        setIsLoading(true);
        setIsProjectLoaded(false);

        try {
            const projectData: PfmeaProject | null = await loadProject(projectName);

            if (projectData) {
                console.log(`[AppContext] Project loaded successfully:`, projectData.metadata);

                // Update PFMEA Items (with AP/RPN recalculation)
                const loadedItems: PfmeaItem[] = (projectData.pfmeaItems || []).map((item: any) => ({
                    ...item,
                    preventionControls: item.preventionControls || [],
                    detectionControls: item.detectionControls || [],
                    recommendedActions: item.recommendedActions || [],
                    actionPriority: calculateActionPriority(item.severity || 1, item.occurrence || 1, item.detection || 1),
                    rpn: calculateRPN(item.severity || 1, item.occurrence || 1, item.detection || 1),
                }));
                setPfmeaItems(loadedItems);

                // Update Process Flow
                if (projectData.processFlow) {
                    setProcessFlow(projectData.processFlow);
                } else {
                    setProcessFlow(null);
                }

                // Update Control Plans
                if (projectData.controlPlan) {
                    setControlPlans([projectData.controlPlan]);
                } else {
                    setControlPlans([]);
                }

                // Update Standard based on project metadata
                const projectStandard = projectData.metadata.fmeaStandard as PfmeaStandard;
                if (projectStandard && STANDARD_CONFIGS[projectStandard]) {
                    setActiveStandard(projectStandard);
                }

                // Clear AIAG-VDA specific data (will be reloaded if available)
                setScoping(undefined);
                setProcessFunctions([]);
                setProductRequirements([]);

                setIsProjectLoaded(true);
                setIsLoading(false);
                return true;
            } else {
                console.warn(`[AppContext] No data returned for project: ${projectName}`);
                setIsLoading(false);
                return false;
            }
        } catch (error) {
            console.error(`[AppContext] Error loading project ${projectName}:`, error);
            setIsLoading(false);
            return false;
        }
    }, []);

    /**
     * Clear all project data (reset to empty state)
     * Used when switching projects or logging out
     */
    const clearProjectData = useCallback(() => {
        console.log('[AppContext] Clearing project data');
        setPfmeaItems([]);
        setProcessFlow(null);
        setControlPlans([]);
        setScoping(undefined);
        setProcessFunctions([]);
        setProductRequirements([]);
        setActiveProject(null);
        setActiveProjectName(null);
        setIsProjectLoaded(false);
    }, []);

    // Get current page context
    const currentPageContext = PAGE_CONTEXTS[location.pathname] || null;

    // Get standard configuration
    const standardConfig = STANDARD_CONFIGS[activeStandard];

    // Run AIAG-VDA Audit to get accurate workflow progress
    // Filter by active project to ensure metrics are project-specific
    const projectFilteredPfmeaItems = activeProjectName
        ? pfmeaItems.filter(item => item.projectName === activeProjectName || item.projectName === activeProject)
        : pfmeaItems;

    const auditReport = runAiagVdaAudit(
        projectFilteredPfmeaItems,
        processFlow,
        controlPlans,
        activeProject,
        scoping,
        processFunctions,
        productRequirements
    );

    // Calculate workflow progress from audit results
    // Status mapping from strict auditor to workflow display:
    // - 'compliant' -> 'completed' (Green - all checks passed)
    // - 'in-progress' -> 'in-progress' (Blue - work in progress, no critical issues)
    // - 'non-conformance' -> 'failed' (Red - critical findings exist)
    // - 'not-started' -> 'not-started' (Gray - no work done)
    const workflowSteps: WorkflowStep[] = AIAG_VDA_WORKFLOW.map((step, idx) => {
        const auditStep = auditReport.stepResults[idx];
        const completedPercentage = auditStep?.score || 0;
        let status: 'not-started' | 'in-progress' | 'completed' | 'failed' = 'not-started';

        if (auditStep?.status === 'compliant') {
            status = 'completed';
        } else if (auditStep?.status === 'non-conformance') {
            status = 'failed';  // Critical findings - Red status
        } else if (auditStep?.status === 'in-progress' || completedPercentage > 0) {
            status = 'in-progress';
        }

        return { ...step, completedPercentage: Math.round(completedPercentage), status };
    });

    // Calculate dashboard metrics
    // Helper function to check if a PFMEA item is complete
    const isPfmeaItemComplete = (item: PfmeaItem): boolean => {
        return !!(
            item.processStepNumber &&
            item.processStepName &&
            item.processFunction &&
            item.failureMode &&
            item.failureEffectEnd &&
            item.failureCause &&
            item.severity > 0 &&
            item.occurrence > 0 &&
            item.detection > 0 &&
            (item.preventionControls.length > 0 || item.detectionControls.length > 0)
        );
    };

    // Calculate data quality metrics (using project-filtered items)
    const dataQuality = (() => {
        const items = projectFilteredPfmeaItems;
        const total = items.length;
        if (total === 0) {
            return {
                completePfmeaItems: 0,
                incompletePfmeaItems: 0,
                itemsWithDfmeaLinks: 0,
                itemsWithControlPlanLinks: 0,
                itemsWithProcessStepLinks: 0,
                missingFailureEffects: 0,
                missingControls: 0,
                missingRatings: 0,
                missingActions: 0,
                unclassifiedCharacteristics: 0,
                overallCompleteness: 0,
                incompleteItemIds: [],
            };
        }

        const completePfmeaItems = items.filter(isPfmeaItemComplete).length;
        const incompletePfmeaItems = total - completePfmeaItems;
        const itemsWithDfmeaLinks = items.filter(i => i.linkedBomItemIds && i.linkedBomItemIds.length > 0).length;
        const itemsWithControlPlanLinks = items.filter(i => i.linkedControlPlanId).length;
        const itemsWithProcessStepLinks = items.filter(i => i.linkedProcessStepId).length;
        const missingFailureEffects = items.filter(i => !i.failureEffectEnd || (!i.failureEffectLocal && !i.failureEffectNext)).length;
        const missingControls = items.filter(i => (i.preventionControls?.length || 0) === 0 && (i.detectionControls?.length || 0) === 0).length;
        const missingRatings = items.filter(i => !i.severity || !i.occurrence || !i.detection).length;

        // Strict High Risk Definition for Dashboard (Matches Auditor)
        const highRiskNoActions = items.filter(i => {
            const isHighRisk = i.actionPriority === 'H' ||
                i.severity >= 9 ||
                (i.severity * i.occurrence >= 45) ||
                (i.severity * i.detection >= 45) ||
                (i.severity * i.occurrence * i.detection > 100);

            return isHighRisk && (!i.recommendedActions || i.recommendedActions.length === 0);
        }).length;

        // AUDITOR RED FLAGS (Strict Linkage & Safety Checks)
        // -----------------------------------------------------

        // 1. Safety Violations: S>=9 MUST have actions (Absolute Rule)
        const safetyViolations = items.filter(i => i.severity >= 9 && (!i.recommendedActions || i.recommendedActions.length === 0)).length;

        // 2. Orphan Criticals: CC items in PFMEA must have Control Plan linkage
        const orphanCriticals = items.filter(i =>
            i.specialCharacteristic === 'CC' && !i.linkedControlPlanId
        ).length;

        // 3. Orphaned Process Steps: Steps in Flow but not in PFMEA
        // Note: Needs strict matching of step IDs or Numbers
        const pfmeaStepIds = new Set(items.map(i => i.linkedProcessStepId).filter(Boolean));
        const pfmeaStepNumbers = new Set(items.map(i => i.processStepNumber).filter(Boolean));

        const orphanedProcessSteps = processFlow?.steps ? processFlow.steps.filter(step => {
            // Check if step is linked by ID or Number
            const isLinkedById = step.id && pfmeaStepIds.has(step.id);
            const isLinkedByNumber = step.stepNumber && pfmeaStepNumbers.has(step.stepNumber);
            return !isLinkedById && !isLinkedByNumber;
        }).length : 0;

        const unclassifiedCharacteristics = items.filter(i => !i.specialCharacteristic || i.specialCharacteristic === 'none').length;
        const incompleteItemIds = items.filter(i => !isPfmeaItemComplete(i)).map(i => i.id);

        // Use the comprehensive Audit Report score as the single source of truth for completeness
        // This ensures the Dashboard matches the Compliance Auditor page
        const overallCompleteness = auditReport.overallScore;

        return {
            completePfmeaItems,
            incompletePfmeaItems,
            itemsWithDfmeaLinks,
            itemsWithControlPlanLinks,
            itemsWithProcessStepLinks,
            missingFailureEffects,
            missingControls,
            missingRatings,
            missingActions: highRiskNoActions,
            safetyViolations,       // New
            orphanCriticals,        // New
            orphanedProcessSteps,   // New
            unclassifiedCharacteristics,
            overallCompleteness,
            incompleteItemIds,
        };
    })();

    const metrics: DashboardMetrics = {
        totalPfmeaItems: projectFilteredPfmeaItems.length,
        byActionPriority: {
            high: projectFilteredPfmeaItems.filter(i => i.actionPriority === 'H').length,
            medium: projectFilteredPfmeaItems.filter(i => i.actionPriority === 'M').length,
            low: projectFilteredPfmeaItems.filter(i => i.actionPriority === 'L').length,
        },
        by4M: {
            Man: projectFilteredPfmeaItems.filter(i => i.processElement === 'Man').length,
            Machine: projectFilteredPfmeaItems.filter(i => i.processElement === 'Machine').length,
            Material: projectFilteredPfmeaItems.filter(i => i.processElement === 'Material').length,
            Method: projectFilteredPfmeaItems.filter(i => i.processElement === 'Method').length,
        },
        bySpecialCharacteristic: {
            CC: projectFilteredPfmeaItems.filter(i => i.specialCharacteristic === 'CC').length,
            SC: projectFilteredPfmeaItems.filter(i => i.specialCharacteristic === 'SC').length,
            none: projectFilteredPfmeaItems.filter(i => i.specialCharacteristic === 'none' || !i.specialCharacteristic).length,
        },
        openActions: projectFilteredPfmeaItems.flatMap(i => i.recommendedActions).filter(a => a.status === 'open').length,
        completedActions: projectFilteredPfmeaItems.flatMap(i => i.recommendedActions).filter(a => a.status === 'completed').length,
        overdueActions: projectFilteredPfmeaItems.flatMap(i => i.recommendedActions)
            .filter(a => a.status === 'open' && new Date(a.targetDate) < new Date()).length,
        processSteps: processFlow?.steps?.length || processFlow?.nodes?.length || 0,
        controlPlanItems: controlPlans.reduce((sum, cp) => sum + cp.items.length, 0),
        averageSeverity: projectFilteredPfmeaItems.length > 0
            ? Math.round((projectFilteredPfmeaItems.reduce((sum, i) => sum + i.severity, 0) / projectFilteredPfmeaItems.length) * 10) / 10
            : 0,
        highRiskItems: projectFilteredPfmeaItems
            .filter(i => i.actionPriority === 'H' || (i.severity >= 9 && i.occurrence >= 3))
            .slice(0, 5),
        recentChanges: [
            { id: '1', type: 'added', description: 'Added PFMEA item: Wire pinched during housing closure', timestamp: new Date('2025-01-01'), user: 'system' },
            { id: '2', type: 'completed', description: 'Completed action: Add wire routing clips to housing design', timestamp: new Date('2025-01-01'), user: 'system' },
        ],
        dataQuality,
    };

    // CRUD Operations
    const addPfmeaItem = (item: PfmeaItem) => {
        const withAP = {
            ...item,
            actionPriority: calculateActionPriority(item.severity, item.occurrence, item.detection),
            rpn: calculateRPN(item.severity, item.occurrence, item.detection),
        };
        setPfmeaItems(prev => [...prev, withAP]);
    };

    const updatePfmeaItem = (id: string, updates: Partial<PfmeaItem>) => {
        setPfmeaItems(prev => prev.map(item => {
            if (item.id !== id) return item;
            const updated = { ...item, ...updates };
            return {
                ...updated,
                actionPriority: calculateActionPriority(updated.severity, updated.occurrence, updated.detection),
                rpn: calculateRPN(updated.severity, updated.occurrence, updated.detection),
            };
        }));
    };

    const deletePfmeaItem = (id: string) => {
        setPfmeaItems(prev => prev.filter(item => item.id !== id));
    };

    const value: AppContextType = {
        activeProject,
        setActiveProject,
        activeProjectName,
        setActiveProjectName,
        projects,
        loadProjectData,       // NEW
        clearProjectData,      // NEW
        isProjectLoaded,       // NEW
        activeStandard,
        setActiveStandard,
        standardConfig,
        pfmeaItems,
        setPfmeaItems,
        addPfmeaItem,
        updatePfmeaItem,
        deletePfmeaItem,
        processFlow,
        setProcessFlow,
        controlPlans,
        setControlPlans,
        scoping,
        setScoping,
        processFunctions,
        setProcessFunctions,
        productRequirements,
        setProductRequirements,
        metrics,
        workflowSteps,
        currentPageContext,
        isLoading,
        setIsLoading,
        isFullScreen,
        setIsFullScreen,
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};
